import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import { store } from "@/app/store";
import { useAdpSessionStore } from "@/features/adp/hooks/useAdpSessionStore";
import {
    useKickoffDqStatsMutation,
    useTriggerDataProfilingMutation,
} from "@/features/adp/mutations/useSourceMutations";
import { closeOverlayAsync } from "@/features/adp/state/overlay.thunks";
import {
    DataProfilingConfirmationOverlayData,
    DataProfilingFieldEntry,
    openOverlay,
} from "@/features/adp/state/overlaySlice";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/ui/alert-dialog";
import { toast } from "sonner";

type SourceFileEntry = {
    source_file_id?: number;
    source_file_field_id?: number;
    field_name?: string;
    field_data_type?: string;
};

type SourceDataShape = {
    source_files?: SourceFileEntry[];
    source_selected_table?: string;
    source_name?: string;
};

function extractProfilingTargets(node: Record<string, unknown> | null): {
    sourceFileId: number | null;
    fields: DataProfilingFieldEntry[];
    tableName: string | null;
    sourceName: string | null;
} {
    const sourceData = (node?.sourceData as { data?: SourceDataShape } | undefined)?.data;
    const files = Array.isArray(sourceData?.source_files) ? sourceData.source_files : [];
    let sourceFileId: number | null = null;
    const fields: DataProfilingFieldEntry[] = [];

    for (const file of files) {
        if (!file || typeof file !== "object") continue;
        if (sourceFileId == null && typeof file.source_file_id === "number") {
            sourceFileId = file.source_file_id;
        }
        if (
            typeof file.source_file_field_id === "number" &&
            typeof file.field_name === "string" &&
            typeof file.field_data_type === "string"
        ) {
            fields.push({
                source_file_field_id: file.source_file_field_id,
                field_name: file.field_name,
                field_data_type: file.field_data_type,
            });
        }
    }

    return {
        sourceFileId,
        fields,
        tableName: sourceData?.source_selected_table ?? null,
        sourceName: sourceData?.source_name ?? null,
    };
}

const DataProfilingConfirmation = () => {
    const { isOpen, type, data } = useAppSelector((state) => state.overlay);
    const isVisible = isOpen && type === "data-profiling-confirmation";
    const overlayData = isVisible ? (data as DataProfilingConfirmationOverlayData) : null;
    const dispatch = useAppDispatch();
    const { getNode } = useAdpSessionStore();
    const kickoffDqStatsMutation = useKickoffDqStatsMutation();
    const triggerDataProfilingMutation = useTriggerDataProfilingMutation();
    const isStarting = kickoffDqStatsMutation.isPending || triggerDataProfilingMutation.isPending;

    const handleSkip = () => {
        toast.success("Source configuration completed successfully");
        dispatch(closeOverlayAsync());
    };

    const handleContinue = async () => {
        const nodeId = overlayData?.nodeId;
        if (!nodeId) {
            toast.error("Source node could not be resolved.");
            return;
        }

        const { sourceFileId, fields, tableName, sourceName } = extractProfilingTargets(getNode(nodeId));
        if (!sourceFileId || fields.length === 0) {
            toast.error("Source file is not registered yet. Please save the source first.");
            return;
        }

        const sessionId = store.getState().pipeline.sessionId;

        try {
            await kickoffDqStatsMutation.mutateAsync({
                source_file_id: [sourceFileId],
                session_id: [sessionId],
            });
            // Fire the spark profiling request — completion is signalled via WebSocket dq_stats.
            await triggerDataProfilingMutation.mutateAsync({
                source_file_id: sourceFileId,
                session_id: sessionId,
                profiling_request_type: "source",
            });
            dispatch(
                openOverlay({
                    type: "data-profiling",
                    data: {
                        nodeId,
                        sessionId,
                        sourceFileId,
                        sourceName,
                        tableName,
                        fields,
                    },
                }),
            );
        } catch {
            // apiRequest's axios interceptor already toasts on HTTP error, and
            // envelope errors (success:false) re-throw with the backend message
            // — the mutation's error path is handled there. Nothing to do here.
        }
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            dispatch(closeOverlayAsync());
        }
    };

    return (
        <AlertDialog open={isVisible} onOpenChange={handleOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Data Profiling</AlertDialogTitle>
                    <AlertDialogDescription>
                        Continue with Data Profiling?
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={handleSkip} disabled={isStarting}>
                        No
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={handleContinue} disabled={isStarting}>
                        {isStarting ? "Starting..." : "Yes"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default DataProfilingConfirmation;
